import { ActivatedRouteSnapshot, Routes } from '@angular/router';
import { ProfileComponent } from './profile/profile.component';
import { MsalGuard } from '@azure/msal-angular';
import { LoginFailedComponent } from './login-failed/login-failed.component';
import { HomeDashboardComponent } from './home-dashboard/home-dashboard.component';
import { CaseFormComponent } from './case-form/case-form.component';
import { ProblemRecordsComponent } from './problem-records/problem-records.component';
import { ProblemRecordsFormComponent } from './problem-records-form/problem-records-form.component';
import { NewsProfileComponent } from './profile/news/news-profile.component';
import { ConfigureInformationComponent } from './profile/configure-info/configure-information.component';
import { ImpersonateUserComponent } from './profile/impersonate-user/impersonate-user.component';
import { KnowledgeLibraryComponent } from './knowledge-library/knowledge-library.component';
import { HelpPageComponent } from './help/help-page.component';
import { HelpDocumentViewAllComponent } from './help/help-document-view-all/help-document-view-all.component';
import { GlobalSearchDatatableComponent } from './shared/components/global-search-datatable/global-search-datatable.component';
import { NewsDetailsComponent } from './news-details/news-details.component';
import { NewsAndAnnouncementsComponent } from './news-and-announcements/news-and-announcements.component';
import { RolesAccessGuard } from './shared/router/roles-access.guard';
import { USER_ROLES } from './shared/enums/user-roles.enum';
import { canDeactivateGuard } from './shared/guards/can-deactivate.guard';
import { BreadcrumbsConstructor } from './shared/router/breadcrumbs.constructor';
import { BreadcrumbItem } from './shared/models/breadcrumb.model';

const DASHBOARD_BREADCRUMBS: BreadcrumbItem[] = [{ label: 'label.dashboard', url: '/', icon: 'bi bi-house' }];
const CASE_BREADCRUMBS_CREATE: BreadcrumbItem[] = [...DASHBOARD_BREADCRUMBS, { label: 'label.create_new_case', url: '/', icon: '' }];
const CASE_BREADCRUMBS_EDIT: BreadcrumbItem[] = [...DASHBOARD_BREADCRUMBS, { label: 'label.case', url: '/', icon: '' }];
const PROBLEM_RECORDS_BREADCRUMBS: BreadcrumbItem[] = [...DASHBOARD_BREADCRUMBS, { label: 'label.problem_records', url: '/problem-records', icon: '' }];
const PROBLEM_RECORDS_BREADCRUMBS_CREATE: BreadcrumbItem[] = [...PROBLEM_RECORDS_BREADCRUMBS, { label: 'title.problem_records.create_new', url: '/', icon: '' }];
const PROBLEM_RECORDS_BREADCRUMBS_EDIT: BreadcrumbItem[] = [...PROBLEM_RECORDS_BREADCRUMBS, { label: 'label.problem_record', url: '/', icon: '' }];
const GLOBAL_SEARCH_BREADCRUMBS: BreadcrumbItem[] = [...DASHBOARD_BREADCRUMBS, { label: 'label.search', url: '/', icon: '' }];
const HELP_BREADCRUMBS: BreadcrumbItem[] = [...DASHBOARD_BREADCRUMBS, { label: 'label.need_help', url: '/help-page', icon: '' }];
const HELP_DOCUMENT_LIBRARY_BREADCRUMBS: BreadcrumbItem[] = [...HELP_BREADCRUMBS, { label: 'label.document_library', url: '/', icon: '' }];
const KNOWLEDGE_LIBRARY_BREADCRUMBS: BreadcrumbItem[] = [...DASHBOARD_BREADCRUMBS, { label: 'label.knowledge_library', url: '/', icon: '' }];
const ADMIN_BREADCRUMBS: BreadcrumbItem[] = [...DASHBOARD_BREADCRUMBS, { label: 'label.admin', url: '/admin', icon: '' }];
const PROFILE_BREADCRUMBS: BreadcrumbItem[] = [...ADMIN_BREADCRUMBS, { label: 'label.profile', url: '/admin', icon: '' }];
const NEWS_BREADCRUMBS_ADMIN: BreadcrumbItem[] = [...ADMIN_BREADCRUMBS, { label: 'label.news', url: '/admin/news', icon: '' }];
const NEWS_BREADCRUMBS_USER: BreadcrumbItem[] = [...DASHBOARD_BREADCRUMBS, { label: 'label.news', url: '/news-announcements', icon: '' }];
const NEWS_BREADCRUMBS_READ: BreadcrumbItem[] = [...NEWS_BREADCRUMBS_USER, { label: 'label.news_information', url: '', icon: '' }];
const NEWS_BREADCRUMBS_EDIT: BreadcrumbItem[] = [...NEWS_BREADCRUMBS_ADMIN, { label: 'label.news_information', url: '/', icon: '' }];
const CONFIGURE_INFORMATION_BREADCRUMBS: BreadcrumbItem[] = [...ADMIN_BREADCRUMBS, { label: 'label.configure_information', url: '', icon: '' }];
const IMPERSONATE_BREADCRUMBS: BreadcrumbItem[] = [...ADMIN_BREADCRUMBS, { label: 'label.impersonate_user', url: '', icon: '' }];

export const routes: Routes = [
  {
    path: 'admin',
    component: ProfileComponent,
    title: 'Admin',
    data: {
      breadcrumbs: ADMIN_BREADCRUMBS,
      accessibleFor: [
        USER_ROLES.ADMIN_USER,
        USER_ROLES.REGULAR_USER,
        USER_ROLES.REGIONAL_TECH_SUPPORT
      ]
    },
    canActivate: [MsalGuard, RolesAccessGuard, BreadcrumbsConstructor],
    children: [
      {
        path: '', pathMatch: 'full', component: ProfileComponent,
        title: 'Profile',
        data: {
          breadcrumbs: PROFILE_BREADCRUMBS,
          accessibleFor: [
            USER_ROLES.ADMIN_USER,
            USER_ROLES.REGULAR_USER,
            USER_ROLES.REGIONAL_TECH_SUPPORT
          ]
        },
        canActivate: [MsalGuard, RolesAccessGuard, BreadcrumbsConstructor]
      },
      {
        path: 'news', component: NewsProfileComponent, 
        title: 'News',
        data: {
          breadcrumbs: NEWS_BREADCRUMBS_ADMIN,
          accessibleFor: [
            USER_ROLES.ADMIN_USER
          ]
        },
        canActivate: [MsalGuard, RolesAccessGuard, BreadcrumbsConstructor]
      },
      {
        path: 'configure-information', component: ConfigureInformationComponent,
        title: 'Configure Information',
        data: {
          breadcrumbs: CONFIGURE_INFORMATION_BREADCRUMBS,
          accessibleFor: [
            USER_ROLES.ADMIN_USER
          ],
        },
        canActivate: [MsalGuard, RolesAccessGuard, BreadcrumbsConstructor],
        canDeactivate: [canDeactivateGuard],
      },
      {
        path: 'impersonate-user', component: ImpersonateUserComponent, 
        title: 'Impersonate Users',
        data: {
          breadcrumbs: IMPERSONATE_BREADCRUMBS,
          accessibleFor: [
            USER_ROLES.ADMIN_USER
          ]
        },
        canActivate: [MsalGuard, RolesAccessGuard, BreadcrumbsConstructor]
      },
    ],
  },
  {
    path: 'admin/news/:id',
    component: NewsProfileComponent,
    title: 'News Details',
    data: {
      breadcrumbs: NEWS_BREADCRUMBS_EDIT,
      accessibleFor: [
        USER_ROLES.ADMIN_USER
      ]
    },
    canActivate: [MsalGuard, RolesAccessGuard, BreadcrumbsConstructor]
  },
  {
    path: 'admin/news/edit/:id',
    title: 'Edit News',
    component: NewsProfileComponent,
    data: {
      breadcrumbs: NEWS_BREADCRUMBS_EDIT,
      accessibleFor: [
        USER_ROLES.ADMIN_USER
      ]
    },
    canActivate: [MsalGuard, RolesAccessGuard, BreadcrumbsConstructor]
  },
  {
    path: 'login-failed',
    title: 'Login Failed',
    component: LoginFailedComponent,
    data: {
      breadcrumbs: DASHBOARD_BREADCRUMBS
    }
  },
  {
    path: 'case-form',
    title: 'Create Case',
    component: CaseFormComponent,
    canActivate: [MsalGuard, BreadcrumbsConstructor],
    canDeactivate: [canDeactivateGuard],
    data: { breadcrumbs: CASE_BREADCRUMBS_CREATE }
  },
  {
    path: 'case-form-by-serial',
    title: 'Create Case',
    component: CaseFormComponent,
    canActivate: [MsalGuard, BreadcrumbsConstructor],
    canDeactivate: [canDeactivateGuard],
    data: { breadcrumbs: CASE_BREADCRUMBS_CREATE }
  },
  {
    path: 'case-form/:id',
    title: 'Case',
    component: CaseFormComponent,
    canActivate: [MsalGuard, BreadcrumbsConstructor],
    canDeactivate: [canDeactivateGuard],
    data: {
      breadcrumbs: CASE_BREADCRUMBS_EDIT
    }
  },
  {
    path: 'problem-records',
    title: 'Problem Records',
    component: ProblemRecordsComponent,
    canActivate: [MsalGuard, BreadcrumbsConstructor],
    data: { breadcrumbs: PROBLEM_RECORDS_BREADCRUMBS }
  },
  {
    path: 'new-problem-record',
    title: 'Create Problem Record',
    component: ProblemRecordsFormComponent,
    canActivate: [MsalGuard, BreadcrumbsConstructor],
    canDeactivate: [canDeactivateGuard],
    data: { breadcrumbs: PROBLEM_RECORDS_BREADCRUMBS_CREATE }
  },
  {
    path: 'problem-record/:id',
    title: 'Problem Record',
    component: ProblemRecordsFormComponent,
    canActivate: [MsalGuard, BreadcrumbsConstructor],
    canDeactivate: [canDeactivateGuard],
    data: {
      breadcrumbs: PROBLEM_RECORDS_BREADCRUMBS_EDIT
    }
  },
  {
    path: 'global-search/:gridUrl',
    title: 'Search',
    component: GlobalSearchDatatableComponent,
    canActivate: [MsalGuard, BreadcrumbsConstructor],
    data: {
      breadcrumbs: GLOBAL_SEARCH_BREADCRUMBS
    }
  },
  {
    path: 'knowledge-library',
    title: 'Knowledge Library',
    component: KnowledgeLibraryComponent,
    canActivate: [MsalGuard, BreadcrumbsConstructor],
    data: { breadcrumbs: KNOWLEDGE_LIBRARY_BREADCRUMBS }
  },
  {
    path: 'help-page',
    title: 'Help Page',
    component: HelpPageComponent,
    canActivate: [MsalGuard, BreadcrumbsConstructor],
    data: { breadcrumbs: HELP_BREADCRUMBS }
  },
  {
    path: 'help-page/help-document-view-all',
    title: 'Document Library',
    component: HelpDocumentViewAllComponent,
    canActivate: [MsalGuard, BreadcrumbsConstructor],
    data: {
      breadcrumbs: HELP_DOCUMENT_LIBRARY_BREADCRUMBS
    }
  },
  {
    path: 'news-announcements',
    title: 'News',
    component: NewsAndAnnouncementsComponent,
    canActivate: [MsalGuard, BreadcrumbsConstructor],
    data: { breadcrumbs: NEWS_BREADCRUMBS_USER }
  },
  {
    path: 'news-details/:id',
    title: 'News Details',
    component: NewsDetailsComponent,
    canActivate: [MsalGuard, BreadcrumbsConstructor],
    data: {
      breadcrumbs: NEWS_BREADCRUMBS_READ
    }
  },
  {
    path: '',
    title: 'Dashboard',
    component: HomeDashboardComponent,
    canActivate: [MsalGuard, BreadcrumbsConstructor],
    data: { breadcrumbs: DASHBOARD_BREADCRUMBS }
  },
];
